import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { workdStorage } from '../storage/workdStorage';

/**
 * Auth user interface
 */
export interface AuthUser {
	id: number;
	instanceUserId?: number;
	username: string;
	email?: string;
	firstName?: string;
	lastName?: string;
	phoneNumber?: string;
	languagePreference?: string;
	roles?: Array<string>;
}

/**
 * Auth instance interface
 */
export interface AuthInstance {
	id: number;
	alias: string;
	name: string;
	active: boolean;
}

/**
 * Auth store state
 */
export interface AuthStoreState {
	// Auth state
	isAuthenticated: boolean;
	isLoading: boolean;
	isAuthInitialized: boolean;
	user: AuthUser | null;
	instance: AuthInstance | null;
	accessToken: string | null;
	refreshToken: string | null;
	expiresAt: number | null;
	authMode: 'jwt' | 'apikey';
	apiKey: string | null;

	// Additional auth-related state
	returnUrl: string | null;
	refreshLock: boolean;
	pkceVerifier: string | null;

	// OAuth flow state (for various OAuth integrations)
	oauthState: string | null;
	oauthErrorHandled: boolean;
	ringcentralOAuthState: string | null;
	qboOAuthState: string | null;
	vonageOAuthState: string | null;
	emailOAuthProvider: string | null;

	// Tab coordination
	tabId: string | null;

	// Delegation state
	isDelegated: boolean;
	originalAuthState: AuthStoreState | null;
}

/**
 * Auth store actions
 */
export interface AuthStoreActions {
	setAuth: (state: Partial<AuthStoreState>) => void;
	setUser: (user: AuthUser | null) => void;
	setInstance: (instance: AuthInstance | null) => void;
	setTokens: (
		accessToken: string | null,
		refreshToken: string | null,
		expiresAt: number | null,
	) => void;
	setAuthenticated: (isAuthenticated: boolean) => void;
	setLoading: (isLoading: boolean) => void;
	setAuthInitialized: (isAuthInitialized: boolean) => void;
	setReturnUrl: (url: string | null) => void;
	setRefreshLock: (locked: boolean) => void;
	setPkceVerifier: (verifier: string | null) => void;
	setOAuthState: (state: string | null) => void;
	setOAuthErrorHandled: (handled: boolean) => void;
	setRingcentralOAuthState: (state: string | null) => void;
	setQboOAuthState: (state: string | null) => void;
	setVonageOAuthState: (state: string | null) => void;
	setEmailOAuthProvider: (provider: string | null) => void;
	setTabId: (tabId: string | null) => void;
	setDelegation: (
		isDelegated: boolean,
		originalState: AuthStoreState | null,
	) => void;
	clearAuth: () => void;
}

const initialState: AuthStoreState = {
	isAuthenticated: false,
	isLoading: false,
	isAuthInitialized: false,
	user: null,
	instance: null,
	accessToken: null,
	refreshToken: null,
	expiresAt: null,
	authMode: 'jwt',
	apiKey: null,
	returnUrl: null,
	refreshLock: false,
	pkceVerifier: null,
	oauthState: null,
	oauthErrorHandled: false,
	ringcentralOAuthState: null,
	qboOAuthState: null,
	vonageOAuthState: null,
	emailOAuthProvider: null,
	tabId: null,
	isDelegated: false,
	originalAuthState: null,
};

/**
 * Zustand auth store with persistence
 */
export const useAuthStore = create<AuthStoreState & AuthStoreActions>()(
	persist(
		(set) => ({
			...initialState,

			setAuth: (state) => {
				set((prev) => ({ ...prev, ...state }));
			},

			setUser: (user) => {
				set({ user });
			},

			setInstance: (instance) => {
				set({ instance });
			},

			setTokens: (accessToken, refreshToken, expiresAt) => {
				set({ accessToken, refreshToken, expiresAt });
			},

			setAuthenticated: (isAuthenticated) => {
				set({ isAuthenticated });
			},

			setLoading: (isLoading) => {
				set({ isLoading });
			},

			setAuthInitialized: (isAuthInitialized) => {
				set({ isAuthInitialized });
			},

			setReturnUrl: (returnUrl) => {
				set({ returnUrl });
			},

			setRefreshLock: (refreshLock) => {
				set({ refreshLock });
			},

			setPkceVerifier: (pkceVerifier) => {
				set({ pkceVerifier });
			},

			setOAuthState: (oauthState) => {
				set({ oauthState });
			},

			setOAuthErrorHandled: (oauthErrorHandled) => {
				set({ oauthErrorHandled });
			},

			setRingcentralOAuthState: (ringcentralOAuthState) => {
				set({ ringcentralOAuthState });
			},

			setQboOAuthState: (qboOAuthState) => {
				set({ qboOAuthState });
			},

			setVonageOAuthState: (vonageOAuthState) => {
				set({ vonageOAuthState });
			},

			setEmailOAuthProvider: (emailOAuthProvider) => {
				set({ emailOAuthProvider });
			},

			setTabId: (tabId) => {
				set({ tabId });
			},

			setDelegation: (isDelegated, originalAuthState) => {
				set({ isDelegated, originalAuthState });
			},

			clearAuth: () => {
				set(initialState);
			},
		}),
		{
			name: 'workd:auth:state',
			storage: workdStorage,
			merge: (persistedState, currentState) => ({
				...currentState,
				...(persistedState as Partial<AuthStoreState>),
				// Always reset oauthErrorHandled on hydration - this is a session-only flag
				// to prevent redirect loops, not meant to persist across browser sessions
				oauthErrorHandled: false,
				// Always reset isAuthInitialized on hydration - consumers must wait for
				// AuthService.loadStoredAuth() to validate/refresh tokens before making API calls
				isAuthInitialized: false,
			}),
			partialize: (state) => ({
				// Only persist essential auth state, not transient flags
				isAuthenticated: state.isAuthenticated,
				user: state.user,
				instance: state.instance,
				accessToken: state.accessToken,
				refreshToken: state.refreshToken,
				expiresAt: state.expiresAt,
				authMode: state.authMode,
				apiKey: state.apiKey,
				returnUrl: state.returnUrl,
				isDelegated: state.isDelegated,
				originalAuthState: state.originalAuthState,
				pkceVerifier: state.pkceVerifier,
				// OAuth state - persist so flows survive page refresh
				oauthState: state.oauthState,
				// Note: oauthErrorHandled is intentionally NOT persisted - it should reset on
				// page refresh to allow fresh auth attempts after returning from being away
				ringcentralOAuthState: state.ringcentralOAuthState,
				qboOAuthState: state.qboOAuthState,
				vonageOAuthState: state.vonageOAuthState,
				emailOAuthProvider: state.emailOAuthProvider,
				// Tab ID - not persisted (sessionStorage would be better, but we clear on logout anyway)
			}),
		},
	),
);
